/**
 * QRCodeAuth - WhatsApp QR code authentication component
 * Displays QR code for scanning with WhatsApp mobile app
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, RefreshCw, Smartphone } from 'lucide-react';

interface QRCodeAuthProps {
  qrDataUrl: string | null;
  isLoading: boolean;
  onRefresh: () => void;
}

export function QRCodeAuth({ qrDataUrl, isLoading, onRefresh }: QRCodeAuthProps) {
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    if (!qrDataUrl) return;
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onRefresh();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [qrDataUrl, onRefresh]);

  return (
    <Card className="mx-auto max-w-md">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <Smartphone className="h-5 w-5" />
          Conectar WhatsApp
        </CardTitle>
        <CardDescription>
          Escanea el código QR con WhatsApp en tu teléfono para conectar
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4">
        {isLoading ? (
          <div className="flex h-64 w-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : qrDataUrl ? (
          <>
            <img
              src={qrDataUrl}
              alt="WhatsApp QR Code"
              className="h-64 w-64 rounded-lg border"
            />
            <p className="text-sm text-muted-foreground">
              El código expira en {countdown}s
            </p>
          </>
        ) : (
          <div className="flex h-64 w-64 flex-col items-center justify-center gap-3 rounded-lg border border-dashed">
            <p className="text-sm text-muted-foreground">
              No hay código QR disponible
            </p>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Reintentar
            </Button>
          </div>
        )}
        <div className="space-y-2 text-center text-sm text-muted-foreground">
          <p>1. Abre WhatsApp en tu teléfono</p>
          <p>2. Ve a Ajustes → Dispositivos vinculados</p>
          <p>3. Toca &quot;Vincular un dispositivo&quot;</p>
          <p>4. Escanea este código QR</p>
        </div>
      </CardContent>
    </Card>
  );
}
